import { RouteProp } from '@react-navigation/native';
import React, { useState } from 'react';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { useScreenParamReader } from '../../../../../hooks/useScreenParamStorage';
import { useOrganizationClientRecords } from '../../../../../hooks/api/useOrganizationClientRecords';
import useOrganizationInvitationDefinitions from '../../../../../hooks/api/useOrganizationInvitationDefinitions';
import { FormDefaults } from './types';
import FormDefaultSelection from './FormDefaultSelection';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import InviteForms from './InviteForms';
import { withRemountOnFocus } from '../../../../HOC/withRemountOnFocus';
import InviteProgress from './InviteProgress';

export type BulkInviteClientsScreenOwnProps = {};

export type BulkInviteClientsScreenProps = BulkInviteClientsScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'bulk-invite-clients'>;
};

const BulkInviteClientsScreen: React.FC<BulkInviteClientsScreenProps> = (props) => {
    const goBack = useGoBack();
    const profilesNavigation = useProfilesTabNavigation();
    const { isLoading, value } = useScreenParamReader({
        key: props.route.params.clientRecordsStorageKey,
        onInvalidData: () => {
            profilesNavigation.navigate('root', {});
        },
        isDataValid: (data: unknown): data is string[] => {
            return (
                Array.isArray(data) && data.length > 0 && data.every((clientRecord) => typeof clientRecord === 'string')
            );
        },
    });
    const {
        isBulkAddingClientRecords,
        bulkLoadedClientRecords,
        bulkInviteClientRecordProgress,
        bulkInviteClientRecords,
        isBulkInvitingClientRecords,
    } = useOrganizationClientRecords({
        bulkLoadOrganizationClientRecordIds: value,
        onBulkInviteClientRecordsComplete: goBack,
    });
    const { invitationDefinitions, isLoadingInvitationDefinitions } = useOrganizationInvitationDefinitions({});

    const isLoadingData = isLoading || isLoadingInvitationDefinitions || isBulkAddingClientRecords;
    const [formDefaults, setFormDefaults] = useState<FormDefaults | null>(null);

    const renderContent = () => {
        if (isBulkInvitingClientRecords) {
            return (
                <InviteProgress bulkInviteProgress={bulkInviteClientRecordProgress} inviteCount={value?.length || 0} />
            );
        } else if (!formDefaults) {
            return (
                <FormDefaultSelection
                    invitationDefinitions={invitationDefinitions}
                    onDefaultSelection={(formDefaults) => {
                        setFormDefaults(formDefaults);
                    }}
                />
            );
        } else {
            return (
                <InviteForms
                    formDefaults={formDefaults}
                    invitationDefinitions={invitationDefinitions}
                    clientRecords={bulkLoadedClientRecords}
                    onInvite={bulkInviteClientRecords}
                    onUndoFormDefaults={() => {
                        setFormDefaults(null);
                    }}
                />
            );
        }
    };

    return <AnimateLoadedContent isLoading={isLoadingData}>{renderContent}</AnimateLoadedContent>;
};

export default React.memo(withRemountOnFocus(BulkInviteClientsScreen));
